import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { ApiError } from '@/api/errors';
import { sessionMe } from '@/api/resources';
import { clearPrivateQueryCache, queryClient } from '@/cache/queryClient';
import { notifySessionStarted } from '@/cache/data-revision-coordinator';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import type { LoginResume } from '@/lib/oauth-consent';
import type { User } from '@/lib/types';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Required'),
});

type FormValues = z.infer<typeof schema>;

/**
 * `resume`: where a sign-in returns to instead of `/`: the consent request an app (Claude) sent
 * the person here from (`/login?next=connect&request=<id>`), or the creative files page Claude
 * linked to (`/login?next=creative-assets&creative=<id>`) (routes/login.tsx).
 */
export function LoginPage({ resume = null }: { resume?: LoginResume | null }) {
  const navigate = useNavigate();
  const required = useRequiredFeedback();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    shouldFocusError: false,
  });

  const mutation = useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: (body: FormValues) => api.post<{ user: User }>('/auth/login', { body }),
    onSuccess: async (data) => {
      notifySessionStarted();
      await clearPrivateQueryCache();
      queryClient.setQueryData(sessionMe.key(undefined), data.user);
      if (resume?.next === 'connect') void navigate({ to: '/connect', search: { request: resume.request } });
      else if (resume?.next === 'creative-assets') {
        void navigate({ to: '/creatives/$creativeId/assets', params: { creativeId: resume.creative } });
      } else void navigate({ to: '/' });
    },
  });

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.code === 'invalid_credentials' || mutation.error.status === 401
        ? 'Invalid email or password.'
        : mutation.error.message
      : null;

  return (
    <div className="auth-shell" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
      <form
        onSubmit={handleSubmit(
          (values) => {
            required.clearAll();
            mutation.mutate(values);
          },
          (formErrors) => {
            required.report([
              formErrors.email && {
                key: 'login-email',
                message: String(formErrors.email.message || 'Enter a valid email'),
              },
              formErrors.password && {
                key: 'login-password',
                message: String(formErrors.password.message || 'Required'),
              },
            ]);
          },
        )}
        className="panel"
        style={{ width: 360, padding: 22 }}
      >
        <h1 className="page-title" style={{ fontSize: 22, marginBottom: 4 }}>SHUBERT ADS MANAGER</h1>
        <div className="muted" style={{ fontSize: 12, marginBottom: 18 }}>
          {resume?.next === 'connect'
            ? 'Sign in to continue connecting an app to your account.'
            : resume?.next === 'creative-assets'
              ? "Sign in to add this creative's files."
              : 'Sign in to continue.'}
        </div>

        <RequiredField
          fieldKey="login-email"
          message={required.messages['login-email'] || errors.email?.message}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            className="input"
            data-validation-focus
            {...register('email', {
              onChange: (event) => {
                if (schema.shape.email.safeParse(event.target.value).success) {
                  required.clear('login-email');
                }
              },
            })}
          />
        </RequiredField>

        <RequiredField
          fieldKey="login-password"
          message={required.messages['login-password'] || errors.password?.message}
          attempt={required.attempt}
          className="field"
        >
          <label className="field-lbl" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className="input"
            data-validation-focus
            {...register('password', {
              onChange: (event) => {
                if (event.target.value) required.clear('login-password');
              },
            })}
          />
        </RequiredField>

        {serverError && <div className="field-hint" style={{ color: 'var(--bad)', marginTop: 8 }}>{serverError}</div>}

        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="btn btn-primary"
          style={{ marginTop: 14, width: '100%', justifyContent: 'center' }}
        >
          {mutation.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
